/**
 * Envelope estándar de los listados paginados server-side
 * ({ items, total, page, pageSize } · espejo del backend).
 */
export interface Paginado<T> {
  items: T[];
  total: number;
  page: number;
  pageSize: number;
}

export interface UserInfo {
  id: string;
  email: string;
  fullName: string;
  roles: string[];
  /** RBAC (Ruta B) · permisos efectivos (unión de los permisos de sus roles). */
  permisos: string[];
}

export interface AuthResponse {
  accessToken: string;
  accessTokenExpiresAt: string;
  refreshToken: string;
  refreshTokenExpiresAt: string;
  user: UserInfo;
}

/** Reto del login en dos pasos (P36) · el password pasó, falta el código TOTP. */
export interface LoginChallenge {
  requiresTwoFactor: true;
  twoFactorToken: string;
}

/** Setup del segundo factor · clave + URI para el QR. */
export interface Setup2FA {
  claveCompartida: string;
  otpauthUri: string;
}

/** Resultado de activar 2FA · códigos de recuperación de un solo uso (se muestran UNA vez). */
export interface Enable2FA {
  codigosRecuperacion: string[];
}

export interface Estado2FA {
  habilitado: boolean;
}

export interface LoginRequest {
  email: string;
  password: string;
}

export interface RefreshRequest {
  refreshToken: string;
}

export interface ApiProblem {
  type?: string;
  title?: string;
  status?: number;
  detail?: string;
  errors?: Record<string, string[]>;
}

// === Sprint 1 · Solicitudes de Pago ===

export type TipoSolicitud = 1 | 2 | 3;
export const TipoSolicitudValores = {
  FB60: 1 as TipoSolicitud,
  ContratoAnualizado: 2 as TipoSolicitud,
  OrdenDeCompra: 3 as TipoSolicitud,
} as const;

export interface SolicitudResumen {
  id: string;
  numeroSolicitud: number;
  /**
   * Folio formateado para mostrar al usuario:
   * - Sin organización confirmada: 11 dígitos (ej. "00000000123")
   * - Con organización confirmada: 4 chars (NumeroSociedad) + 11 chars = 15 chars
   *   (ej. "5300" + "00000000123" → "530000000000123")
   */
  folio: string;
  estado: string;
  fechaInicio: string;
}

export interface OrganizacionEnSolicitud {
  id: string;
  rfc: string;
  numeroSociedad: string;
  nombre: string;
}

/** P44 · FB60 Contrapago · catálogo de organizaciones (`GET /catalogos/organizaciones`). */
export interface OrganizacionCatalogo {
  id: string;
  rfc: string;
  numeroSociedad: string;
  nombre: string;
  estaActiva: boolean;
}

export interface CFDIDeSolicitud {
  id: string;
  numeroSlot: number;
  uuid: string;
  /** Folio del CFDI · usado para empatar la dispersión multi-factura por Excel. */
  folio: string;
  version: string;
  tipoComprobante: string;
  moneda: string;
  fechaEmision: string;
  fechaTimbrado: string | null;
  total: number;
  subtotal: number;
  rfcEmisor: string;
  nombreEmisor: string;
  rfcReceptor: string;
  nombreReceptor: string;
  cargadoAt: string;
  statusFiscal: string;
  statusFiscalCodigo: string | null;
  statusFiscalMensajeOriginal: string | null;
  statusFiscalMensajeAmigable: string | null;
  statusFiscalConsultadoAt: string | null;
  // Detalle enriquecido de la consulta al SAT (vigencia · cancelación · EFOS).
  // Presente solo cuando hay una consulta registrada; "Desconocido" cuando fue con el mock.
  satProvider: string | null;
  satEstadoComprobante: string | null;
  satEsCancelable: string | null;
  satEstatusCancelacion: string | null;
  satValidacionEfos: string | null;
  satConsultadoAt: string | null;
}

export interface ReglaError {
  codigo: string;
  mensaje: string;
}

export interface DatosGenerales {
  motivoPago: string;
  periodoServicioInicio: string;
  periodoServicioFin: string;
  comentarios: string | null;
  areaId: string;
  areaNombre: string;
}

export interface ProveedorAsignado {
  numeroProveedor: number;
  rfc: string;
  razonSocial: string;
}

/** Contrato Anualizado (CA) · snapshot del contrato anualizado asociado a la solicitud. */
export interface ContratoAnualizado {
  clave: string;
  proveedorRfc: string;
  proveedorNombre: string;
  proveedorNumero: number;
  total: number;
  moneda: string;
}

/** Orden de Compra (OC) · snapshot de la orden de compra asociada a la solicitud. */
export interface OrdenDeCompra {
  clave: string;
  proveedorRfc: string;
  proveedorNombre: string;
  proveedorNumero: number;
  total: number;
  moneda: string;
}

/** Equivalencia de unidad SAT↔SAP · catálogo admin para la conciliación de OC. */
export interface EquivalenciaUnidad {
  id: string;
  claveUnidadSat: string;
  unidadSap: string;
  descripcion: string | null;
  activo: boolean;
}

/** Ajuste de configuración de la app (tabla administracion.configuraciones). */
/** Tipo de dato del valor de una Configuracion · determina el control del gestor admin. */
export type TipoAjusteConfiguracion =
  | "Texto"
  | "Entero"
  | "Decimal"
  | "Booleano"
  | "Fecha"
  | "FechaHora"
  | "Secreto";

export interface Configuracion {
  clave: string;
  valor: string;
  categoria: string;
  descripcion: string | null;
  valorDefault: string | null;
  tipo: TipoAjusteConfiguracion;
  actualizadoAt: string;
}

export interface SolicitudDetalle {
  id: string;
  numeroSolicitud: number;
  /**
   * Folio formateado para mostrar al usuario · ver doc en `SolicitudResumen.folio`.
   * Backend computa: sin org → 11 chars, con org → 15 chars (sociedad + folio).
   */
  folio: string;
  solicitanteId: string;
  solicitanteNombre: string | null;
  tipo: string | null;
  subtipo: string | null;
  estado: string;
  fechaInicio: string;
  fechaUltimoEvento: string;
  organizacion: OrganizacionEnSolicitud | null;
  cfdi: CFDIDeSolicitud | null;
  cfdis: CFDIDeSolicitud[];
  erroresReglas: ReglaError[];
  datosGenerales: DatosGenerales | null;
  proveedor: ProveedorAsignado | null;
  checklist: ChecklistDocumentos;
  flujo: FlujoDeAutorizacion | null;
  ultimoMotivoRechazo: string[];
  motivosBaja: string[];
  /**
   * Nombre del usuario de Mesa de Control que dio de baja la solicitud ·
   * visible al solicitante junto con los motivos (decisión 38). Null si no
   * fue dada de baja o si terminó por otra vía (ej. cancelación propia).
   */
  dadaDeBajaPorNombre: string | null;
  /** Momento de la baja por Mesa de Control · null si no aplica. */
  dadaDeBajaAt: string | null;
  enviadaAContabilizacion: boolean;
  enviadaAContabilizacionAt: string | null;
  nombreArchivoSftp: string | null;
  contratoAnualizado: ContratoAnualizado | null;
  ordenCompra: OrdenDeCompra | null;
  /** Fecha de pago (ISO `YYYY-MM-DD`) fijada por Mesa de Control al autorizar el último nivel. */
  fechaPago: string | null;
  /**
   * P44 · FB60 Contrapago · "en espera de CFDI" es PARALELO al `estado`, igual
   * patrón que `enviadaAContabilizacion`. `esperaCfdiFechaLimite` no-nulo indica
   * que la solicitud entró en espera; `esperaCfdiConciliadoAt` no-nulo indica que
   * ya se resolvió (el CFDI real llegó y pasó las reglas de conciliación).
   */
  esperaCfdiFechaLimite: string | null;
  esperaCfdiAvisoEnviadoAt: string | null;
  esperaCfdiVencida: boolean;
  esperaCfdiConciliadoAt: string | null;
  /** Subtipo INVOICE · datos extraídos por OCR de la factura extranjera. Null si no es Invoice. */
  invoice?: DatosInvoiceDetalle | null;
}

/** Subtipo INVOICE · datos extraídos de la factura extranjera (PDF). */
export interface DatosInvoiceDetalle {
  plantilla: string;
  emisorNombre: string;
  emisorTaxId: string | null;
  emisorDireccion: string | null;
  fechaExpedicion: string | null;
  receptorNombre: string | null;
  receptorRfc: string;
  numeroFactura: string | null;
  importeTotal: number;
  moneda: string;
  impuestoImporte: number | null;
  impuestoTasa: number | null;
  conceptos: { descripcion: string; importe: number }[];
  pdfPath: string;
}

export interface NivelDeFlujo {
  id: string;
  nivel: number;
  rolesAutorizadores: string[];
  estado: "Pendiente" | "EnProceso" | "Autorizado";
  iniciadoAt: string | null;
  autorizadoAt: string | null;
  autorizadoPorUsuarioId: string | null;
  autorizadoPorEmail: string | null;
  autorizadoPorNombre: string | null;
}

export interface FlujoDeAutorizacion {
  id: string;
  nivelActual: number;
  cantidadNiveles: number;
  estado: "Resuelto" | "EnProceso" | "Completado";
  resueltoAt: string;
  completadoAt: string | null;
  niveles: NivelDeFlujo[];
}

export interface BandejaAutorizacionItem {
  id: string;
  numeroSolicitud: number;
  /** Folio formateado · 11 chars sin org, 15 chars con org (sociedad + folio). */
  folio: string;
  estado: string;
  tipo: string | null;
  subtipo: string | null;
  organizacionNombre: string | null;
  areaNombre: string | null;
  proveedorRazonSocial: string | null;
  proveedorNumero: number | null;
  rfcEmisor: string | null;
  descripcionPrimerConcepto: string | null;
  totalCfdi: number | null;
  monedaCfdi: string | null;
  nivelActual: number;
  cantidadNiveles: number;
  fechaCreacion: string;
  fechaUltimoEvento: string;
}

/** Página del listado de la bandeja · espejo de BandejaPageDto del backend. */
export type BandejaAutorizacionPage = Paginado<BandejaAutorizacionItem>;

export interface LineaPolizaApi {
  numeroLinea: number;
  claveSat: string;
  descripcion: string;
  centroDeCostoCodigo: string;
  cuentaContableCodigo: string;
  importe: number;
}

export interface PolizaApi {
  id: string;
  numeroSolicitud: number;
  numeroCfdi: number;
  cfdiUuid: string;
  numeroProveedor: number;
  razonSocialProveedor: string;
  numeroSociedad: string;
  organizacionNombre: string;
  totalDebe: number;
  moneda: string;
  estado: "Generada" | "DepositadaEnSftp" | "ErrorEnDeposito";
  nombreArchivoSftp: string | null;
  errorDeposito: string | null;
  generadaAt: string;
  depositadaAt: string | null;
  lineas: LineaPolizaApi[];
}

export interface MiSolicitud {
  id: string;
  numeroSolicitud: number;
  /** Folio formateado · ver `SolicitudResumen.folio`. */
  folio: string;
  estado: string;
  tipo: string | null;
  subtipo: string | null;
  organizacionNombre: string | null;
  /** Área a la que pertenece · null hasta que se capturan datos generales (paso 3). */
  areaNombre: string | null;
  /** Razón social del proveedor asignado · null hasta el paso 4 del wizard. */
  proveedorRazonSocial: string | null;
  /** Número de proveedor (catálogo) · null hasta el paso 4. */
  proveedorNumero: number | null;
  /** RFC del proveedor asignado (snapshot del catálogo) · null hasta el paso 4. */
  proveedorRfc: string | null;
  /**
   * Descripción del primer concepto del XML del CFDI cargado · backend la
   * envía completa (hasta 500 chars). El frontend la trunca a 25 chars para
   * la columna del listado con tooltip para ver completa.
   */
  descripcionPrimerConcepto: string | null;
  totalCfdi: number | null;
  monedaCfdi: string | null;
  rfcEmisor: string | null;
  cantidadDocumentos: number | null;
  flujoNivelActual: number | null;
  flujoCantidadNiveles: number | null;
  /** Roles autorizadores del nivel actual (CSV) · para etiquetar el estado. */
  flujoRolActual: string | null;
  tieneRechazoPendiente: boolean;
  enviadaAContabilizacion: boolean;
  fechaInicio: string;
  fechaUltimoEvento: string;
  /** Fecha de pago (ISO `YYYY-MM-DD`) · null hasta que la solicitud queda Autorizada. */
  fechaPago: string | null;
  /**
   * Nombre del solicitante dueño · solo viene resuelto (backend vía
   * `IUserDirectory`) cuando el usuario actual ve solicitudes de otros
   * (Mesa de Control/Auditor/Administrador). Null para el propio
   * solicitante viendo sus solicitudes.
   */
  solicitanteNombre: string | null;
}

export interface MensajeDeChat {
  id: string;
  solicitudId: string;
  autorUsuarioId: string;
  rolAutor: "Solicitante" | "Autorizador" | "MesaDeControl";
  texto: string;
  nivelDelFlujo: number | null;
  enviadoAt: string;
}

export type CategoriaDocumento =
  | "FacturaXml"
  | "FacturaPdf"
  | "Evidencia"
  | "Contrato"
  | "Adicional"
  | "CertificadoResidencia";

export interface DocumentoVinculado {
  id: string;
  documentoId: string;
  categoria: string;
  nombreOriginal: string;
  vinculadoAt: string;
  /** Tipo del catálogo (P31) · solo para la categoría Adicional. */
  tipoDocumento?: string | null;
}

/** Tipo de documento ADICIONAL del catálogo (P31 · /api/catalogos/tipos-documento). */
export interface TipoDocumentoAdicional {
  id: string;
  nombre: string;
  orden: number;
  activo: boolean;
}

export interface ChecklistDocumentos {
  facturaXmlCargados: number;
  facturaPdfCargados: number;
  adicionalesCargados: number;
  certificadoResidenciaCargados: number;
  otrosCargados: number;
  completoFB60: boolean;
  documentos: DocumentoVinculado[];
}

export interface DocumentoApi {
  id: string;
  solicitudId: string;
  numeroSolicitud: number;
  numeroCfdi: number;
  categoria: string;
  nombreOriginal: string;
  extension: string;
  tamanoBytes: number;
  rutaStorage: string;
  subidoPorUsuarioId: string;
  cargadoAt: string;
  /** Tipo del catálogo (P31) · solo para la categoría Adicional. */
  tipoDocumento?: string | null;
}

export interface CargarCFDIResultado {
  solicitud: SolicitudDetalle;
  reglasAutorizadas: boolean;
  erroresReglas: ReglaError[];
}

// === Sprint 2 · Catálogos y prorrateo ===

export interface Area {
  id: string;
  nombre: string;
  descripcion: string | null;
  /** Habilita la carga de la dispersión de CeCos por plantilla Excel en el prorrateo. */
  dispersionExcelHabilitada?: boolean;
}

export interface ProveedorCatalogo {
  numeroProveedor: number;
  rfc: string;
  razonSocial: string;
  datosBancarios: { banco: string; cuenta: string; clabe: string | null };
  estaActivo: boolean;
}

export interface ClaveSat {
  clave: string;
  descripcion: string;
}

export interface CentroDeCosto {
  id: string;
  codigo: string;
  nombre: string;
}

export interface CuentaContable {
  id: string;
  codigo: string;
  nombre: string;
}

/** P45 · catálogo de indicadores de impuesto SAP (V0/V5/V7). */
export interface IndicadorImpuesto {
  id: string;
  codigo: string;
  tasaOCuota: number;
  descripcion: string;
  activo: boolean;
}

export type TipoLineaProrrateo = "Automatica" | "Manual";

export type ModoProrrateo = "Conceptos" | "Manual";

export interface LineaProrrateo {
  id: string;
  numeroLinea: number;
  /** Número del concepto al que pertenece la sub-línea (0 en modo Manual/Contrapago). */
  conceptoNumero: number;
  /** Importe objetivo inmutable del concepto · las sub-líneas del concepto suman esto. */
  importeConcepto: number;
  tipo: TipoLineaProrrateo;
  claveSat: string;
  claveSatDescripcion: string;
  descripcion: string;
  centroDeCostoId: string;
  centroDeCostoCodigo: string;
  cuentaContableId: string;
  cuentaContableCodigo: string;
  importe: number;
  /** P45 · código del catálogo de indicadores de impuesto (V0/V5/V7) · snapshot de la línea. */
  indicador: string;
}

export type EstadoProrrateo = "Construido" | "Confirmado";

export interface Prorrateo {
  id: string;
  solicitudId: string;
  numeroCfdi: number;
  /** "Conceptos" (FB60/CA/OC · divisible) o "Manual" (Contrapago · líneas a mano). */
  modo: ModoProrrateo;
  estado: EstadoProrrateo;
  sumaTotal: number;
  /** Histórico · hoy siempre true (todos los prorrateos por conceptos son divisibles). */
  editablePorUsuario: boolean;
  lineas: LineaProrrateo[];
  /**
   * El área de la solicitud permite cargar la dispersión de CeCos por plantilla
   * Excel (paridad legado · flag por área). El wizard muestra el menú de Excel
   * solo cuando es true.
   */
  dispersionExcelHabilitada?: boolean;
}

// === Sprint 1 · Admin SAT mock ===

export type SatOverrideEstado = 1 | 2 | 3 | 4;
export const SatOverrideEstadoValores = {
  Vigente: 1 as SatOverrideEstado,
  Rechazado: 2 as SatOverrideEstado,
  Suspendido: 3 as SatOverrideEstado,
  NoConsultable: 4 as SatOverrideEstado,
} as const;

export interface SatRespuestaInput {
  estado: SatOverrideEstado;
  codigo: string | null;
  mensajeOriginal: string | null;
  mensajeAmigable: string | null;
}

export interface SatOverride {
  id: string;
  uuid: string | null;
  esDefault: boolean;
  estado: SatOverrideEstado;
  codigo: string | null;
  mensajeOriginal: string | null;
  mensajeAmigable: string | null;
  creadoAt: string;
}

// === Sprint 7 · Admin · catálogos ===

export interface OrganizacionAdmin {
  id: string;
  rfc: string;
  numeroSociedad: string;
  nombre: string;
  estaActiva: boolean;
}

export interface UsuarioOrgRef {
  id: string;
  nombre: string;
  numeroSociedad: string;
}

export interface UsuarioAdmin {
  id: string;
  email: string;
  fullName: string | null;
  roles: string[];
  areas: string[];
  organizaciones: UsuarioOrgRef[];
  activo: boolean;
}

export interface DatosBancariosAdmin {
  banco: string;
  cuenta: string;
  clabe: string | null;
}

export interface ProveedorAdmin {
  numeroProveedor: number;
  rfc: string;
  razonSocial: string;
  datosBancarios: DatosBancariosAdmin;
  estaActivo: boolean;
  /** Proveedor extranjero · el RFC es un tax ID libre (sin formato mexicano). */
  esExtranjero: boolean;
}

// ===== Admin · Compras SAP (Órdenes de Compra / Contratos Anualizados) =====

export interface ImpuestoCompraAdmin {
  id: number;
  tipo: string;
  indicador: string | null;
  monto: number;
  porcentaje: number;
}

export interface ConceptoCompraAdmin {
  id: number;
  lineaPedido: string;
  cantidad: number;
  unidad: string | null;
  noIdentificacion: string | null;
  descripcion: string;
  valorUnitario: number;
  importe: number;
  observaciones: string | null;
  centroCostos: string | null;
  descCecos: string | null;
  cuentaContable: string | null;
  impuestos: ImpuestoCompraAdmin[];
}

export interface ProveedorCompraAdmin {
  nombre: string;
  rfc: string;
  clave: string | null;
  calle: string | null;
  noInterior: string | null;
  noExterior: string | null;
  localidad: string | null;
  municipio: string | null;
  estado: string | null;
  pais: string | null;
  codigoPostal: string | null;
}

export interface EntradaAlmacenAdmin {
  folio: string;
  claveOrdenCompra: string;
  fecha: string | null;
  subTotal: number;
  impuestos: number;
  total: number;
  moneda: string;
  anula: string | null;
  referencia: string | null;
  observaciones: string | null;
  conceptos: ConceptoCompraAdmin[];
}

export interface OrdenCompraAdmin {
  clave: string;
  version: number;
  division: string | null;
  estatus: string | null;
  fecha: string | null;
  subTotal: number;
  impuestos: number;
  total: number;
  moneda: string;
  observaciones: string | null;
  activo: boolean;
  proveedor: ProveedorCompraAdmin;
  conceptos: ConceptoCompraAdmin[];
  entradas: EntradaAlmacenAdmin[];
}

export interface OrdenCompraResumen {
  id: string;
  clave: string;
  version: number;
  anioFiscal: number | null;
  proveedorNombre: string;
  proveedorRfc: string;
  estatus: string | null;
  fecha: string | null;
  total: number;
  moneda: string;
  conceptos: number;
  entradas: number;
  activo: boolean;
}

export interface ContratoAnualizadoAdmin {
  clave: string;
  version: number;
  division: string | null;
  estatus: string | null;
  fecha: string | null;
  subTotal: number;
  impuestos: number;
  total: number;
  moneda: string;
  observaciones: string | null;
  activo: boolean;
  proveedor: ProveedorCompraAdmin;
  conceptos: ConceptoCompraAdmin[];
}

export interface ContratoAnualizadoResumen {
  id: string;
  clave: string;
  version: number;
  anioFiscal: number | null;
  proveedorNombre: string;
  proveedorRfc: string;
  estatus: string | null;
  fecha: string | null;
  total: number;
  moneda: string;
  conceptos: number;
  activo: boolean;
}

/** Cuántas solicitudes de pago referencian un CA (para avisar al editar/dar de baja). */
export interface UsoContratoAnualizado {
  solicitudesRelacionadas: number;
  solicitudesVigentes: number;
}

// ----- Inputs (bodies POST/PUT) -----

export interface ImpuestoCompraInput {
  tipo: string;
  indicador: string | null;
  monto: number;
  porcentaje: number;
}

export interface ConceptoCompraInput {
  lineaPedido: string;
  cantidad: number;
  unidad: string | null;
  noIdentificacion: string | null;
  descripcion: string;
  valorUnitario: number;
  importe: number;
  observaciones: string | null;
  centroCostos: string | null;
  descCecos: string | null;
  cuentaContable: string | null;
  impuestos: ImpuestoCompraInput[];
}

export interface ProveedorCompraInput {
  nombre: string;
  rfc: string;
  clave: string | null;
  calle: string | null;
  noInterior: string | null;
  noExterior: string | null;
  localidad: string | null;
  municipio: string | null;
  estado: string | null;
  pais: string | null;
  codigoPostal: string | null;
}

export interface EntradaAlmacenInput {
  folio: string;
  claveOrdenCompra: string;
  fecha: string | null;
  subTotal: number;
  impuestos: number;
  total: number;
  moneda: string;
  anula: string | null;
  referencia: string | null;
  observaciones: string | null;
  conceptos: ConceptoCompraInput[];
}

export interface OrdenCompraInput {
  division: string | null;
  estatus: string | null;
  fecha: string | null;
  subTotal: number;
  impuestos: number;
  total: number;
  moneda: string;
  observaciones: string | null;
  proveedor: ProveedorCompraInput;
  conceptos: ConceptoCompraInput[];
  entradas: EntradaAlmacenInput[];
}

export interface ContratoAnualizadoInput {
  division: string | null;
  estatus: string | null;
  fecha: string | null;
  subTotal: number;
  impuestos: number;
  total: number;
  moneda: string;
  observaciones: string | null;
  proveedor: ProveedorCompraInput;
  conceptos: ConceptoCompraInput[];
}
